import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { TagBadge } from "../components/atoms/TagBadge";
import { Typography } from "../components/atoms/Typography";
import { KbSectionContentPreview } from "../components/molecules/KbSectionContentPreview";
import { GameRuleTemplateStepsSidebar } from "../components/organisms/GameRuleTemplateStepsSidebar";
import {
  buildKbGenerationFieldSteps,
  getKbGenerationTemplate,
} from "../services/gameRuleKbGenerationTemplateService";
import type {
  KbGenerationFieldStep,
  KbGenerationTemplateDetail,
} from "../types/gameRuleKbGenerationTemplate";

export const GameRuleTemplateDetailPage = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();

  const [template, setTemplate] = useState<KbGenerationTemplateDetail | null>(null);
  const [steps, setSteps] = useState<readonly KbGenerationFieldStep[]>([]);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const id = templateId?.trim();
    if (!id) {
      setError("Template id is missing.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const detail = await getKbGenerationTemplate(id);
        const fieldSteps =
          detail.sectionIds.length > 0
            ? (await buildKbGenerationFieldSteps(detail.sectionIds)).filter(
                (step) => step.id !== "programme",
              )
            : [];
        if (cancelled) return;
        setTemplate(detail);
        setSteps(fieldSteps);
        setSelectedStepId(fieldSteps.length > 0 ? fieldSteps[0].id : null);
      } catch (e) {
        if (!cancelled) {
          setTemplate(null);
          setSteps([]);
          setSelectedStepId(null);
          setError(e instanceof Error ? e.message : "Unable to load template detail.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const selectedStep = useMemo(
    () => steps.find((s) => s.id === selectedStepId) ?? null,
    [steps, selectedStepId],
  );

  const handleSelectStep = useCallback((stepId: string) => {
    setSelectedStepId(stepId);
  }, []);

  if (loading) {
    return (
      <section className="flex min-h-0 flex-1 flex-col">
        <div className="px-6 py-4">
          <Typography variant="muted">Loading template…</Typography>
        </div>
      </section>
    );
  }

  if (error || !template) {
    return (
      <section className="flex min-h-0 flex-1 flex-col">
        <div className="px-6 py-4">
          <button
            type="button"
            onClick={() => navigate("/rules/templates")}
            className="back-link"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            Back to Templates
          </button>
          <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error ?? "Template not found."}
          </div>
        </div>
      </section>
    );
  }

  const content = selectedStep?.content?.trim() ?? "";

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/rules/templates")}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label="Back to Templates"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <Typography as="h1" variant="h1">
              {template.name}
            </Typography>
            <Typography variant="muted">
              v{template.version} · {template.sectionCount}{" "}
              {template.sectionCount === 1 ? "section" : "sections"}
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <GameRuleTemplateStepsSidebar
          steps={steps}
          selectedStepId={selectedStepId}
          onSelectStep={handleSelectStep}
        />
        <div className="flex-1 overflow-auto p-6">
          {selectedStep ? (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Typography as="h2" variant="h2">
                  {selectedStep.name}
                </Typography>
                {selectedStep.tag ? <TagBadge label={selectedStep.tag} /> : null}
              </div>
              {content ? (
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <KbSectionContentPreview content={content} />
                </div>
              ) : (
                <Typography variant="muted">No content for this section.</Typography>
              )}
            </>
          ) : (
            <Typography variant="muted">Select a section to view its content.</Typography>
          )}
        </div>
      </div>
    </section>
  );
};
